import React, { useState, useEffect } from "react";
import SuperAdminLayout from "./SuperAdminLayout";
import {
    SectionHeader,
    TablePro,
    Badge,
    Button,
    CardGlass,
    StatCard
} from "@/components/ui/PremiumComponents";
import {
    Search,
    Terminal,
    ShieldAlert,
    AlertCircle,
    Code,
    Copy,
    ChevronRight,
    ChevronDown,
    Download,
    Activity
} from 'lucide-react';
import { getSAToken, fetchSecurityEvents, fetchDeliveryLogs } from "@/services/superadminApi";
import { toast } from "sonner";
import Loader from "@/components/ui/Loader";

export default function SuperAdminLogs() {
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('security');
    const [logs, setLogs] = useState([]);
    const [selectedLog, setSelectedLog] = useState(null);
    const [search, setSearch] = useState("");

    const loadLogs = async () => {
        setLoading(true);
        try {
            if (activeTab === 'security') {
                const res = await fetchSecurityEvents();
                setLogs(res.items || []);
            } else {
                const res = await fetchDeliveryLogs();
                setLogs(res || []);
            }
        } catch (error) {
            console.error("Error loading logs", error);
            toast.error("Error al cargar logs");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadLogs(); }, [activeTab]);

    const handleExport = async () => {
        try {
            const token = getSAToken();
            const BASE_URL = (process.env.REACT_APP_API_URL || "http://localhost:8000").replace(/\/api\/?$/, "");
            const res = await fetch(`${BASE_URL}/api/superadmin/delivery/exports/logs.csv`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) throw new Error("Export failed");
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `logs_${activeTab}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            toast.success("Logs exportados correctamente");
        } catch (error) {
            toast.error("Error al exportar logs");
        }
    };

    const renderRow = (row) => {
        if (activeTab === 'security') {
            return (
                <>
                    <td className="px-6 py-4">
                        <div className="flex flex-col">
                            <span className="font-bold text-white text-xs">{new Date(row.created_at).toLocaleString()}</span>
                            <span className="text-[10px] font-mono text-white/30 truncate w-32">{row.id || 'N/A'}</span>
                        </div>
                    </td>
                    <td className="px-6 py-4">
                        <Badge status={row.event_type.includes('fail') ? 'danger' : 'warning'}>{row.event_type.toUpperCase()}</Badge>
                    </td>
                    <td className="px-6 py-4">
                        <div className="flex flex-col">
                            <span className="text-white text-xs font-bold">{row.ip || 'Unknown'}</span>
                            <span className="text-[10px] font-black uppercase text-primary">{row.company_id || 'System'}</span>
                        </div>
                    </td>
                    <td className="px-6 py-4">
                        <span className="text-xs text-white/60 truncate max-w-xs block">
                            {typeof row.message === 'object' ? JSON.stringify(row.message) : (row.message || 'Sin mensaje detallado')}
                        </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                        <button onClick={() => setSelectedLog(row)} className="p-2 hover:bg-white/5 rounded-lg text-white/40 hover:text-white"><ChevronRight size={18} /></button>
                    </td>
                </>
            );
        } else {
            return (
                <>
                    <td className="px-6 py-4">
                        <div className="font-bold text-white text-xs">{new Date(row.created_at).toLocaleString()}</div>
                    </td>
                    <td className="px-6 py-4">
                        <Badge status="info">{row.event_type?.toUpperCase()}</Badge>
                    </td>
                    <td className="px-6 py-4">
                        <div className="text-white text-xs font-bold">{row.actor_type} / {row.actor_id}</div>
                    </td>
                    <td className="px-6 py-4 font-mono text-[10px] text-white/40">{row.job_id || row.company_id}</td>
                    <td className="px-6 py-4 text-right">
                        <button onClick={() => setSelectedLog(row)} className="p-2 hover:bg-white/5 rounded-lg text-white/40 hover:text-white"><ChevronRight size={18} /></button>
                    </td>
                </>
            );
        }
    };

    return (
        <SuperAdminLayout>
            <div className="space-y-8">
                <SectionHeader
                    title="Logs e Inteligencia"
                    subtitle="Auditoría en tiempo real de seguridad y red de delivery."
                    actions={
                        <Button variant="secondary" onClick={handleExport} className="flex items-center gap-2">
                            <Download size={16} /> Exportar CSV
                        </Button>
                    }
                />

                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <StatCard label="Eventos 24h" value={logs.length} icon={<Activity size={22} />} trend="+12%" />
                    <StatCard label="Amenazas" value="0" theme="danger" icon={<ShieldAlert size={22} />} />
                    <StatCard label="Requests/s" value="4.2" icon={<Terminal size={22} />} />
                    <StatCard label="Alertas SLA" value="0" icon={<AlertCircle size={22} />} theme="info" />
                </div>

                <div className="flex gap-4 p-1 glass-effect w-fit rounded-2xl border border-white/10">
                    <button onClick={() => setActiveTab('security')} className={`px-6 py-2 rounded-xl text-sm font-black transition-all ${activeTab === 'security' ? 'bg-primary shadow-lg' : 'text-white/40'}`}>Garantía y Seguridad</button>
                    <button onClick={() => setActiveTab('delivery')} className={`px-6 py-2 rounded-xl text-sm font-black transition-all ${activeTab === 'delivery' ? 'bg-primary shadow-lg' : 'text-white/40'}`}>Red de Delivery</button>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
                    <div className="xl:col-span-3 space-y-6">
                        {loading ? <div className="py-20 flex justify-center"><Loader /></div> : (
                            <TablePro
                                headers={activeTab === 'security' ?
                                    ["Timestamp", "Evento", "Origen", "Mensaje", ""] :
                                    ["Timestamp", "Evento", "Actor", "Contexto", ""]
                                }
                                data={logs.filter(l => JSON.stringify(l).toLowerCase().includes(search.toLowerCase()))}
                                renderRow={renderRow}
                            />
                        )}

                        {selectedLog && (
                            <CardGlass
                                title="Detalle Crudo"
                                headerAction={<button onClick={() => setSelectedLog(null)}><ChevronDown size={24} /></button>}
                                className="animate-slide-up"
                            >
                                <div className="mt-6 p-6 bg-black/40 rounded-3xl border border-white/10 font-mono text-[10px] text-cyan-400 overflow-x-auto">
                                    <pre>{JSON.stringify(activeTab === 'security' ? selectedLog : selectedLog, null, 2)}</pre>
                                </div>
                            </CardGlass>
                        )}
                    </div>

                    <div className="space-y-6">
                        <CardGlass title="Búsqueda Local">
                            <div className="relative mt-4">
                                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                                <input
                                    type="text"
                                    placeholder="Filtrar por ID, IP, texto..."
                                    className="w-full bg-white/5 border border-white/10 rounded-xl py-2.5 pl-10 pr-4 text-xs outline-none focus:ring-1 ring-primary/40"
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                />
                            </div>
                        </CardGlass>
                        <CardGlass title="Tips de Seguridad">
                            <ul className="mt-4 space-y-2 text-[11px] text-white/50 italic leading-relaxed">
                                <li>• Los logs de seguridad solo se retienen 90 días por GDPR.</li>
                                <li>• IP repetidas en fallos de login son bloqueadas automáticamente 15min.</li>
                                <li>• Las acciones del SuperAdmin son irreversibles y quedan firmadas.</li>
                            </ul>
                        </CardGlass>
                    </div>
                </div>
            </div>
        </SuperAdminLayout>
    );
}
